import {
  IPayableSchema,
  IPayableCreateRequest,
  IPayableUpdateRequest,
  ClientWorkFlowStatus,
  InvoiceStatus,
  ICollaboratorSchema,
  IInvoiceLineItemsCreateRequest,
  IDeductionCreateRequest,
  DeductionType,
  IServiceStatusResponse,
  IBulkPayableItem
} from "@wingspanhq/payments/dist/interfaces";
import { Currency } from "@wingspanhq/users/dist/lib/interfaces";
import { InvoicesFilter, paymentsService } from "../../../../services/payments";
import { isSameDate } from "../../../../utils/dates";
import { LINE_ITEM_HOUR_RATE } from "../../../utils/invoicesFormUtils";
import { getMatchingCollaborators } from "../../bulkUploadCollaborators/collaboratorUtils";
import { PayableStatus, UploadPayable } from "./BulkUploadPayablesInfo";

// types

export interface PayableLineItem {
  title: string;
  description?: string;
  amount?: string;
  hours?: string;
  hourlyRate?: string;
  reimbursable?: boolean;
  labels?: { [key: string]: string };
}
export interface MergedPayable {
  email?: string;
  collaboratorId?: string;
  contractorId?: string;
  lineItems: PayableLineItem[];
  dueDate: Date;
  invoiceNotes?: string;
  $custom?: { [key: string]: string };
}

export interface ImportDeduction {
  email?: string;
  collaboratorId?: string;
  contractorId?: string;
  dueDate: Date;
  invoiceNotes?: string;
  lineItemTitle: string;
  lineItemDescription?: string;
  amount?: string;
  labels?: { [key: string]: string };
  $custom?: { [key: string]: string };
}

export interface UserFileUploadInfo {
  userId: string;
  batchId: string;
  filename: string;
  isFlatfileV3?: boolean;
}

export type IdentifierKey = "email" | "collaboratorId" | "contractorId";

export interface ToBeCreatedPayable {
  mergedPayable: MergedPayable;
  dbPayable?: IPayableSchema;
  requestPayload: IPayableCreateRequest;
}

export interface ToBeUpdatedPayable {
  mergedPayable: MergedPayable;
  dbPayable: IPayableSchema;
  requestPayload: { id: string } & IPayableUpdateRequest;
}

export interface ToBeCreatedDeduction {
  csvDeduction: ImportDeduction;
  requestPayload: IDeductionCreateRequest;
}
export interface ImportPreviewStats {
  totalLineItemsAmount: number;
  totalDeductionsAmount: number;
  deductionsCollaborators: number;
  totalImportablePayables: number;
  collaboratorsCount: number;
  toBeCreatedPayables: Array<ToBeCreatedPayable>;
  toBeUpdatedPayables: Array<ToBeUpdatedPayable>;
  toBeCreatedDeductions: Array<ToBeCreatedDeduction>;
}

export interface UploadPayablesRequests {
  toBeCreatedPayables: Array<ToBeCreatedPayable>;
  toBeUpdatedPayables: Array<ToBeUpdatedPayable>;
}

// utility functions

export const prepareLineItemsFromPayable = (
  payable: MergedPayable
): UploadPayable[] => {
  return payable.lineItems.map(lineItem => ({
    email: payable.email,
    collaboratorId: payable.collaboratorId,
    contractorId: payable.contractorId,
    dueDate: payable.dueDate,
    invoiceNotes: payable.invoiceNotes,
    amount: lineItem.amount,
    lineItemTitle: lineItem.title,
    lineItemDescription: lineItem.description,
    lineItemHourlyRate: lineItem.hourlyRate,
    lineItemHours: lineItem.hours,
    reimbursable: lineItem.reimbursable
  }));
};

export const prepareLineItemsFromDeduction = (
  deduction: ImportDeduction
): UploadPayable => {
  return {
    email: deduction.email,
    collaboratorId: deduction.collaboratorId,
    contractorId: deduction.contractorId,
    dueDate: deduction.dueDate,
    invoiceNotes: deduction.invoiceNotes,
    amount: deduction.amount,
    lineItemTitle: deduction.lineItemTitle,
    lineItemDescription: deduction.lineItemDescription
  };
};

export const getTotalDeductionsAmount = (
  toBeCreatedDeductions: ToBeCreatedDeduction[]
) => {
  return toBeCreatedDeductions.reduce((total, deduction) => {
    return total + deduction.requestPayload.amount;
  }, 0);
};

export const getTotalLineItemsAmountFromCSV = (
  csvLineItems: UploadPayable[]
) => {
  return csvLineItems.reduce((total, csvLineItem) => {
    const isHourlyRateSet =
      !!csvLineItem.lineItemHourlyRate && !!csvLineItem.lineItemHours;
    const totalCost = isHourlyRateSet
      ? Number(csvLineItem.lineItemHourlyRate) *
        Number(csvLineItem.lineItemHours)
      : Number(csvLineItem.amount?.replaceAll(",", ""));

    // don't add deductions to total
    return total + (totalCost > 0 ? totalCost : 0);
  }, 0);
};

export const getIdentifierKeyFromLineItems = (
  lineItems: UploadPayable[]
): IdentifierKey => {
  if (lineItems.every(li => !!li.email)) {
    return "email";
  } else if (lineItems.every(li => !!li.collaboratorId)) {
    return "collaboratorId";
  } else if (lineItems.every(li => !!li.contractorId)) {
    return "contractorId";
  }
  return "email";
};

const getExistingPayablesForMember = async (
  payableData: MergedPayable,
  existingCollaborators: ICollaboratorSchema[]
) => {
  const matchingCollaborators = getMatchingCollaborators(
    existingCollaborators,
    {
      email: payableData.email,
      collaboratorId: payableData.collaboratorId,
      contractorId: payableData.contractorId
    }
  );
  const matchingCollaborator = matchingCollaborators[0];
  if (!matchingCollaborator) {
    // If the contractor is not on wingspan, don't do anything
    return;
  }
  const filter: InvoicesFilter = {
    memberClientId: {
      in: [matchingCollaborator.collaboratorId]
    },
    status: {
      in: [
        InvoiceStatus.Draft,
        InvoiceStatus.Pending,
        InvoiceStatus.Open,
        InvoiceStatus.Overdue,
        InvoiceStatus.Paid
      ]
    },
    "client.workflowStatus": {
      "!=": ClientWorkFlowStatus.Funded
    }
  };
  const invoiceQuery = {
    filter
  };
  const existingPayablesForMember = await paymentsService.payable.list(
    invoiceQuery
  );
  return existingPayablesForMember.data;
};

const csvLineItemToRequest = (
  csvLineItem: PayableLineItem
): IInvoiceLineItemsCreateRequest => {
  const isHourlyRateSet = !!csvLineItem.hourlyRate && !!csvLineItem.hours;

  return {
    description: csvLineItem.title,
    totalCost: isHourlyRateSet
      ? Number(csvLineItem.hourlyRate) * Number(csvLineItem.hours)
      : Number(csvLineItem.amount?.replaceAll(",", "")),
    detail: csvLineItem.description,
    unit: isHourlyRateSet ? LINE_ITEM_HOUR_RATE : undefined,
    costPerUnit: isHourlyRateSet ? Number(csvLineItem.hourlyRate) : undefined,
    quantity: isHourlyRateSet ? Number(csvLineItem.hours) : undefined,
    reimbursableExpense: csvLineItem.reimbursable,
    ...(csvLineItem.labels && { labels: csvLineItem.labels })
  };
};

const getUpdateLineItemsRequest = (
  dbPayable: IPayableSchema,
  csvPayable: MergedPayable
) => {
  let lineItemsUpdate = [];
  for (let i = 0; i < dbPayable.lineItems.length; i++) {
    lineItemsUpdate.push({});
  }
  lineItemsUpdate = [
    ...lineItemsUpdate,
    ...csvPayable.lineItems.map(csvLineItemToRequest)
  ];
  return lineItemsUpdate;
};

// Flatfile V3 utils

export const canMergeLineItem = (
  payables: MergedPayable[],
  lineItem: UploadPayable,
  identifierKey: IdentifierKey
) => {
  return (
    payables.filter(payable => {
      const isSameDueDate = isSameDate(
        new Date(payable?.dueDate),
        new Date(lineItem?.dueDate)
      );
      return (
        Number(lineItem.amount?.replaceAll(",", "")) >= 0 &&
        isSameDueDate &&
        payable[identifierKey] === lineItem[identifierKey]
      );
    }).length > 0
  );
};

/**
 * Separate the payables and deductions in CSV. Merge the CSV line items
 * if canMerge return true.
 * */

export const preparePayablesAndDeductionsFromCSVLineItems = (
  lineItems: UploadPayable[],
  { filename, userId, batchId }: UserFileUploadInfo
): { payables: MergedPayable[]; deductions: ImportDeduction[] } => {
  const payables: MergedPayable[] = [];
  const deductions: ImportDeduction[] = [];
  const identifierKey = getIdentifierKeyFromLineItems(lineItems);
  lineItems.forEach(lineItem => {
    const canMerge = canMergeLineItem(payables, lineItem, identifierKey);
    const baseObj = {
      email: lineItem.email,
      collaboratorId: lineItem.collaboratorId,
      contractorId: lineItem.contractorId,
      dueDate: lineItem.dueDate,
      invoiceNotes: lineItem.invoiceNotes
    };
    const lineItemLabels = {
      ...(lineItem["$custom"] ? lineItem["$custom"] : {}),
      filename,
      uploadBatchId: batchId,
      uploadedBy: userId,
      uploadedAt: new Date().toISOString()
    };

    if (canMerge) {
      const mergeablePayableIdx = payables.findIndex(payable => {
        const isSameDueDate = isSameDate(
          new Date(payable?.dueDate),
          new Date(lineItem?.dueDate)
        );
        return (
          isSameDueDate && payable[identifierKey] === lineItem[identifierKey]
        );
      });
      if (mergeablePayableIdx !== -1) {
        payables[mergeablePayableIdx] = {
          ...payables[mergeablePayableIdx],
          lineItems: [
            ...payables[mergeablePayableIdx].lineItems,
            {
              amount: lineItem.amount,
              description: lineItem.lineItemDescription,
              title: lineItem.lineItemTitle,
              hours: lineItem.lineItemHours,
              hourlyRate: lineItem.lineItemHourlyRate,
              reimbursable: lineItem.reimbursable,
              labels: lineItemLabels
            }
          ]
        };
      }
    } else {
      const amount = Number(lineItem.amount?.replaceAll(",", ""));
      if (amount < 0) {
        // handle deductions

        const deductionsLabels = {
          ...(lineItem["$custom"] ? lineItem["$custom"] : {}),
          filename,
          uploadBatchId: batchId,
          uploadedBy: userId,
          uploadedAt: new Date().toISOString()
        };

        deductions.push({
          ...baseObj,
          lineItemTitle: lineItem.lineItemTitle,
          lineItemDescription: lineItem.lineItemDescription,
          amount: lineItem.amount,
          labels: deductionsLabels
        });
      } else {
        payables.push({
          ...baseObj,
          lineItems: [
            {
              title: lineItem.lineItemTitle,
              description: lineItem.lineItemDescription,
              amount: lineItem.amount,
              hours: lineItem.lineItemHours,
              hourlyRate: lineItem.lineItemHourlyRate,
              reimbursable: lineItem.reimbursable,
              labels: lineItemLabels
            }
          ]
        });
      }
    }
  });
  return { payables, deductions };
};

const updateAtSortComparator = (
  a: { updatedAt: Date },
  b: { updatedAt: Date }
) => b.updatedAt.getTime() - a.updatedAt.getTime();

const handleDraftPayables = (
  csvPayable: MergedPayable,
  matchedPayablesByCollaboratorIdAndDueDate: IPayableSchema[],
  matchingCollaborator: ICollaboratorSchema,
  paymentsSettings: IServiceStatusResponse
) => {
  const createPayableRequest: IPayableCreateRequest = {
    collaboratorId: matchingCollaborator.collaboratorId,
    lineItems: csvPayable.lineItems.map(csvLineItemToRequest),
    dueDate: csvPayable.dueDate,
    creditFeeHandling: {
      clientPays: 100,
      memberPays: 0
    },
    currency: paymentsSettings.defaultAccountCurrency || Currency.USD,
    invoiceNotes: csvPayable.invoiceNotes
  };
  let toBeCreatedPayables: Array<ToBeCreatedPayable> = [];
  let toBeUpdatedPayables: Array<ToBeUpdatedPayable> = [];
  const matchingDraftPayables = matchedPayablesByCollaboratorIdAndDueDate.filter(
    dbPayable =>
      dbPayable.status === InvoiceStatus.Draft &&
      (dbPayable.client.workflowStatus === ClientWorkFlowStatus.Pending ||
        !Object(dbPayable.client).hasOwnProperty("workflowStatus"))
  );
  if (matchingDraftPayables.length > 0) {
    const latestMatchingDraftPayable = matchingDraftPayables.sort(
      updateAtSortComparator
    )[0];
    const updatePayableRequest = {
      id: latestMatchingDraftPayable.payableId,
      lineItems: getUpdateLineItemsRequest(
        latestMatchingDraftPayable,
        csvPayable
      ),
      invoiceNotes:
        csvPayable.invoiceNotes ?? latestMatchingDraftPayable.invoiceNotes
    };
    toBeUpdatedPayables.push({
      mergedPayable: csvPayable,
      dbPayable: latestMatchingDraftPayable,
      requestPayload: updatePayableRequest
    });
  } else {
    toBeCreatedPayables.push({
      mergedPayable: csvPayable,
      requestPayload: {
        ...createPayableRequest,
        status: InvoiceStatus.Draft
      }
    });
  }
  return { toBeCreatedPayables, toBeUpdatedPayables };
};

const handleOpenPayables = (
  csvPayable: MergedPayable,
  matchedPayablesByCollaboratorIdAndDueDate: IPayableSchema[],
  matchingCollaborator: ICollaboratorSchema,
  paymentsSettings: IServiceStatusResponse
) => {
  const createPayableRequest: IPayableCreateRequest = {
    collaboratorId: matchingCollaborator.collaboratorId,
    lineItems: csvPayable.lineItems.map(csvLineItemToRequest),
    dueDate: csvPayable.dueDate,
    creditFeeHandling: {
      clientPays: 100,
      memberPays: 0
    },
    currency: paymentsSettings.defaultAccountCurrency || Currency.USD,
    invoiceNotes: csvPayable.invoiceNotes
  };
  let toBeCreatedPayables: Array<ToBeCreatedPayable> = [];
  let toBeUpdatedPayables: Array<ToBeUpdatedPayable> = [];
  const matchingOpenPayables = matchedPayablesByCollaboratorIdAndDueDate.filter(
    dbPayable =>
      [InvoiceStatus.Pending, InvoiceStatus.Draft, InvoiceStatus.Open].includes(
        dbPayable.status
      ) &&
      (dbPayable.client.workflowStatus === ClientWorkFlowStatus.Pending ||
        !Object(dbPayable.client).hasOwnProperty("workflowStatus"))
  );
  if (matchingOpenPayables.length > 0) {
    const latestMatchingOpenPayable = matchingOpenPayables.sort(
      updateAtSortComparator
    )[0];
    const updatePayableRequest = {
      id: latestMatchingOpenPayable.payableId,
      lineItems: getUpdateLineItemsRequest(
        latestMatchingOpenPayable,
        csvPayable
      ),
      invoiceNotes:
        csvPayable.invoiceNotes ?? latestMatchingOpenPayable.invoiceNotes,
      status: [InvoiceStatus.Pending, InvoiceStatus.Draft].includes(
        latestMatchingOpenPayable.status
      )
        ? InvoiceStatus.Draft
        : latestMatchingOpenPayable.status
    };
    toBeUpdatedPayables.push({
      mergedPayable: csvPayable,
      dbPayable: latestMatchingOpenPayable,
      requestPayload: updatePayableRequest
    });
  } else {
    toBeCreatedPayables.push({
      mergedPayable: csvPayable,
      requestPayload: createPayableRequest
    });
  }
  return { toBeCreatedPayables, toBeUpdatedPayables };
};

const handleApprovedPayables = (
  csvPayable: MergedPayable,
  matchedPayablesByCollaboratorIdAndDueDate: IPayableSchema[],
  matchingCollaborator: ICollaboratorSchema,
  paymentsSettings: IServiceStatusResponse
) => {
  const createPayableRequest: IPayableCreateRequest = {
    collaboratorId: matchingCollaborator.collaboratorId,
    lineItems: csvPayable.lineItems.map(csvLineItemToRequest),
    dueDate: csvPayable.dueDate,
    creditFeeHandling: {
      clientPays: 100,
      memberPays: 0
    },
    currency: paymentsSettings.defaultAccountCurrency || Currency.USD,
    invoiceNotes: csvPayable.invoiceNotes
  };
  let toBeCreatedPayables: Array<ToBeCreatedPayable> = [];
  let toBeUpdatedPayables: Array<ToBeUpdatedPayable> = [];
  const matchingApprovedPayables = matchedPayablesByCollaboratorIdAndDueDate.filter(
    dbPayable =>
      [InvoiceStatus.Pending, InvoiceStatus.Draft, InvoiceStatus.Open].includes(
        dbPayable.status
      ) &&
      (dbPayable.client.workflowStatus === ClientWorkFlowStatus.Approved ||
        !Object(dbPayable.client).hasOwnProperty("workflowStatus"))
  );
  if (matchingApprovedPayables.length > 0) {
    const latestMatchingApprovedPayable = matchingApprovedPayables.sort(
      updateAtSortComparator
    )[0];

    const updatePayableRequest = {
      id: latestMatchingApprovedPayable.payableId,
      lineItems: getUpdateLineItemsRequest(
        latestMatchingApprovedPayable,
        csvPayable
      ),
      invoiceNotes:
        csvPayable.invoiceNotes ?? latestMatchingApprovedPayable.invoiceNotes,
      status: [InvoiceStatus.Pending, InvoiceStatus.Draft].includes(
        latestMatchingApprovedPayable.status
      )
        ? InvoiceStatus.Draft
        : latestMatchingApprovedPayable.status,
      client: {
        workflowStatus: ClientWorkFlowStatus.Approved
      }
    };
    toBeUpdatedPayables.push({
      mergedPayable: csvPayable,
      dbPayable: latestMatchingApprovedPayable,
      requestPayload: updatePayableRequest
    });
  } else {
    toBeCreatedPayables.push({
      mergedPayable: csvPayable,
      requestPayload: {
        ...createPayableRequest,
        status: InvoiceStatus.Draft
      }
    });
  }
  return { toBeCreatedPayables, toBeUpdatedPayables };
};

const preparePayablesUploadRequestsFromCSVPayables = async (
  csvPayables: MergedPayable[],
  existingCollaborators: ICollaboratorSchema[],
  payableStatus: PayableStatus,
  paymentsSettings: IServiceStatusResponse
): Promise<UploadPayablesRequests> => {
  let toBeCreatedPayables: Array<ToBeCreatedPayable> = [];
  let toBeUpdatedPayables: Array<ToBeUpdatedPayable> = [];

  for (const csvPayable of csvPayables) {
    const matchingCollaborators = getMatchingCollaborators(
      existingCollaborators,
      {
        email: csvPayable.email,
        collaboratorId: csvPayable.collaboratorId,
        contractorId: csvPayable.contractorId
      }
    );
    const matchingCollaborator = matchingCollaborators[0];

    if (!matchingCollaborator) {
      return {
        toBeCreatedPayables: [],
        toBeUpdatedPayables: []
      };
    }
    const existingPayablesForMember = (await getExistingPayablesForMember(
      csvPayable,
      existingCollaborators
    )) as IPayableSchema[];
    const matchedPayablesByCollaboratorIdAndDueDate = existingPayablesForMember.filter(
      dbPayable =>
        isSameDate(new Date(dbPayable.dueDate), new Date(csvPayable.dueDate))
    );

    const createPayableRequest: IPayableCreateRequest = {
      collaboratorId: matchingCollaborator.collaboratorId,
      lineItems: csvPayable.lineItems.map(csvLineItemToRequest),
      dueDate: csvPayable.dueDate,
      creditFeeHandling: {
        clientPays: 100,
        memberPays: 0
      },
      currency: paymentsSettings.defaultAccountCurrency || Currency.USD,
      invoiceNotes: csvPayable.invoiceNotes
    };
    const isPayableAlreadyExists =
      matchedPayablesByCollaboratorIdAndDueDate.length > 0;
    if (isPayableAlreadyExists) {
      switch (payableStatus) {
        case PayableStatus.Draft: {
          const output = handleDraftPayables(
            csvPayable,
            matchedPayablesByCollaboratorIdAndDueDate,
            matchingCollaborator,
            paymentsSettings
          );
          toBeCreatedPayables = output.toBeCreatedPayables;
          toBeUpdatedPayables = output.toBeUpdatedPayables;
          break;
        }
        case PayableStatus.Open: {
          const output = handleOpenPayables(
            csvPayable,
            matchedPayablesByCollaboratorIdAndDueDate,
            matchingCollaborator,
            paymentsSettings
          );
          toBeCreatedPayables = output.toBeCreatedPayables;
          toBeUpdatedPayables = output.toBeUpdatedPayables;
          break;
        }
        case PayableStatus.Approved: {
          const output = handleApprovedPayables(
            csvPayable,
            matchedPayablesByCollaboratorIdAndDueDate,
            matchingCollaborator,
            paymentsSettings
          );
          toBeCreatedPayables = output.toBeCreatedPayables;
          toBeUpdatedPayables = output.toBeUpdatedPayables;
          break;
        }
      }
    } else {
      toBeCreatedPayables.push({
        mergedPayable: csvPayable,
        requestPayload: {
          ...createPayableRequest,
          status: InvoiceStatus.Draft
        }
      });
    }
  }
  return { toBeCreatedPayables, toBeUpdatedPayables };
};

const prepareDeductionsUploadRequestsFromCSVDeductions = (
  csvDeductions: ImportDeduction[],
  existingCollaborators: ICollaboratorSchema[],
  paymentsSettings: IServiceStatusResponse
): ToBeCreatedDeduction[] => {
  const toBeCreatedDeductions: ToBeCreatedDeduction[] = [];
  for (const csvDeduction of csvDeductions) {
    const matchingCollaborators = getMatchingCollaborators(
      existingCollaborators,
      {
        email: csvDeduction.email,
        collaboratorId: csvDeduction.collaboratorId,
        contractorId: csvDeduction.contractorId
      }
    );
    const matchingCollaborator = matchingCollaborators[0];

    if (!matchingCollaborator) {
      return [];
    }
    toBeCreatedDeductions.push({
      csvDeduction,
      requestPayload: {
        memberId: matchingCollaborator.memberId,
        clientId: matchingCollaborator.clientId,
        amount: Math.abs(
          Number(csvDeduction.amount?.replace(/[^0-9.-]+/g, ""))
        ),
        currency: paymentsSettings.defaultAccountCurrency || Currency.USD,
        priority: 1,
        type: DeductionType.PrePayment,
        startDate: csvDeduction.dueDate,
        name:
          csvDeduction.lineItemTitle +
          (csvDeduction.lineItemDescription
            ? ` ${csvDeduction.lineItemDescription}`
            : ""),
        ...(csvDeduction.labels ? { labels: csvDeduction.labels } : {})
      }
    });
  }
  return toBeCreatedDeductions;
};

export const getLineItemsImportPreview = async (
  csvLineItems: UploadPayable[],
  data: UserFileUploadInfo,
  existingCollaborators: ICollaboratorSchema[],
  payableStatus: PayableStatus
) => {
  const paymentsSettings = await paymentsService.service.get();
  const output = preparePayablesAndDeductionsFromCSVLineItems(
    csvLineItems,
    data
  );
  const importablePayables = output.payables;
  const importableDeductions = output.deductions;
  const identifierKey = getIdentifierKeyFromLineItems(csvLineItems);

  const {
    toBeCreatedPayables,
    toBeUpdatedPayables
  } = await preparePayablesUploadRequestsFromCSVPayables(
    importablePayables,
    existingCollaborators,
    payableStatus,
    paymentsSettings
  );
  const toBeCreatedDeductions = prepareDeductionsUploadRequestsFromCSVDeductions(
    importableDeductions,
    existingCollaborators,
    paymentsSettings
  );

  const totalLineItemsAmount = getTotalLineItemsAmountFromCSV(csvLineItems);
  const totalDeductionsAmount = getTotalDeductionsAmount(toBeCreatedDeductions);

  // only count those from payables to be created
  const collaborators = toBeCreatedPayables.map(
    ({ mergedPayable }) => mergedPayable[identifierKey]
  );

  const uniqueCollaborators = [...new Set(collaborators)];
  const deductionCollaborators = toBeCreatedDeductions.map(
    ({ csvDeduction }) => csvDeduction[identifierKey]
  );
  const uniqueDeductionsCollaborators = [...new Set(deductionCollaborators)];

  return {
    totalLineItemsAmount,
    totalDeductionsAmount,
    deductionsCollaborators: uniqueDeductionsCollaborators.length,
    collaboratorsCount: uniqueCollaborators.length,
    totalImportablePayables: importablePayables.length,
    toBeCreatedPayables,
    toBeUpdatedPayables,
    toBeCreatedDeductions
  };
};
